import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Input, Form } from "@arco-design/web-react";

import { IconEdit } from "@arco-design/web-react/icon";
import { CommentApi, PostApi } from "../types/types";
import { MAX_COMMENT_LENGTH } from "../constants";
import { EmojiClickData } from "emoji-picker-react";
import IconFace from "../../../../../assets/icons/IconFace";
import { getButtonTextColor, getButtonColor } from "../../../../../utils/cssVariables";

import { EmojiPickerModal } from "../../../../../components/EmojiPickerModal/EmojiPickerModal";
import { createUpdatedCommentsFormData } from "../../../../../utils/createUpdatedCommentsFormData";

interface IUpdateCommentModalProps {
   comment: CommentApi;
   post: PostApi;
   isMutating: boolean;
   onUpdatePost: (postId: number, updatedPost: FormData) => void;
}

type CommentFormValue = {
  commentValue: string;
}

const UpdateCommentModal = ({ comment, post, isMutating, onUpdatePost }: IUpdateCommentModalProps) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CommentFormValue>();
  const [visible, setVisible] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const contentInputRef = useRef<HTMLTextAreaElement>(null);

  const showModal = () => {
    setVisible(true);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const content = form.getFieldValue('commentValue');
    form.setFieldValue('commentValue', content + emojiData.emoji);
    setShowEmojiPicker(false);

    contentInputRef.current?.focus();
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
  };

  
  const handleUpdate = (formValues: CommentFormValue) => {
    if (post.id) {
      const formData = createUpdatedCommentsFormData({post, comment, content: formValues.commentValue});
      onUpdatePost(post.id, formData);
      setVisible(false);
    }
    form.resetFields();
  };

  const handleClose = () => {
    form.resetFields();
    setVisible(false);
  }

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        commentValue: comment.content,
      });
    }
  }, [visible, comment, form]);

  return (
    <div>
      <Button
        style={{
          padding: 0,
          backgroundColor: "transparent",
        }}
        onClick={showModal}
        disabled={isMutating}
      >
        <IconEdit />
      </Button>

      <Modal
        className="feed-modal"
        title={t('home_page.update_comment')}
        visible={visible}
        footer={null}
        onCancel={handleClose}
      >
        <Form 
          form={form}
          layout="vertical"
          onSubmit={handleUpdate}
          autoComplete="off"
        >
          <Form.Item 
            label={t('home_page.your_comment_input_label')}
            field="commentValue"
            rules={[
              { required: true, message: t('notification_messages.post_content_empty') },
              { max: MAX_COMMENT_LENGTH, message: t('notification_messages.post_content_length_exceeded') }
            ]}
          >
            <Input.TextArea
              placeholder={t('home_page.your_comment_input_placeholder')}
              showWordLimit
              maxLength={MAX_COMMENT_LENGTH}
            />
          </Form.Item>
          <div className="comment-actions__wrapper">
            <div className="comment-actions__content">
              <Button
                className="comment-action-button"
                onClick={toggleEmojiPicker}
              >
                <IconFace className="comment-action-button-icon" />
              </Button>
            </div>
            <Button
              htmlType="submit"
              style={{
                color: getButtonTextColor(),
                backgroundColor: getButtonColor(),
              }}
              shape="round"
            >
              {t('common.update_button')}
            </Button>
            
            <EmojiPickerModal
              visible={showEmojiPicker}
              onClose={() => setShowEmojiPicker(false)}
              onEmojiClick={handleEmojiClick}
            />
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default UpdateCommentModal;